import type { CSSProperties } from "react";
import { motion } from "framer-motion";
import { SupportLinks, type SupportLinkKind } from "./SupportLinks";
import { getTileColor } from "../theme";

const TITLE_SCREEN_TRANSITION = {
  duration: 0.18,
  ease: "easeOut" as const
};

type TitleScreenProps = {
  discordUrl?: string;
  feedbackUrl?: string;
  isLeaderboardEnabled: boolean;
  onStartRun: () => void;
  onOpenLink?: (kind: SupportLinkKind, url: string) => void;
  onOpenLeaderboard: () => void;
  onOpenSettings: () => void;
  supportEmail?: string;
};

type AmbientTileStyle = CSSProperties & {
  "--ambient-tile-color"?: string;
  "--ambient-duration"?: string;
  "--ambient-delay"?: string;
  "--ambient-float-duration"?: string;
  "--ambient-float-delay"?: string;
};

const ambientTiles = [
  {
    value: 2,
    style: {
      top: "-0.35rem",
      left: "-0.35rem",
      "--ambient-duration": "13.5s",
      "--ambient-delay": "0s",
      "--ambient-float-duration": "9.5s",
      "--ambient-float-delay": "-0.8s"
    } satisfies AmbientTileStyle
  },
  {
    value: 5,
    style: {
      top: "12%",
      right: "-0.45rem",
      "--ambient-duration": "15s",
      "--ambient-delay": "-5s",
      "--ambient-float-duration": "10.5s",
      "--ambient-float-delay": "-2.2s"
    } satisfies AmbientTileStyle
  },
  {
    value: 8,
    style: {
      top: "52%",
      left: "-0.4rem",
      "--ambient-duration": "14.5s",
      "--ambient-delay": "-8s",
      "--ambient-float-duration": "11s",
      "--ambient-float-delay": "-4.4s"
    } satisfies AmbientTileStyle
  },
  {
    value: 3,
    style: {
      top: "63%",
      right: "-0.35rem",
      "--ambient-duration": "16s",
      "--ambient-delay": "-10s",
      "--ambient-float-duration": "9.8s",
      "--ambient-float-delay": "-1.7s"
    } satisfies AmbientTileStyle
  },
  {
    value: 7,
    style: {
      bottom: "14%",
      left: "0.5rem",
      "--ambient-duration": "14s",
      "--ambient-delay": "-3s",
      "--ambient-float-duration": "10.8s",
      "--ambient-float-delay": "-6.1s"
    } satisfies AmbientTileStyle
  },
  {
    value: 9,
    style: {
      bottom: "4%",
      right: "1rem",
      "--ambient-duration": "15.5s",
      "--ambient-delay": "-11.5s",
      "--ambient-float-duration": "11.2s",
      "--ambient-float-delay": "-3.8s"
    } satisfies AmbientTileStyle
  }
] as const;

export function TitleScreen({
  discordUrl,
  feedbackUrl,
  isLeaderboardEnabled,
  onStartRun,
  onOpenLink,
  onOpenLeaderboard,
  onOpenSettings,
  supportEmail
}: TitleScreenProps) {
  return (
    <motion.section
      className="menu-screen title-screen"
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -18 }}
      transition={TITLE_SCREEN_TRANSITION}
    >
      <div className="title-screen-ambient" aria-hidden="true">
        {ambientTiles.map(({ value, style }) => (
          <div
            key={value}
            className="ambient-tile"
            style={
              {
                ...style,
                "--ambient-tile-color": getTileColor(value).hex
              } as unknown as CSSProperties
            }
          >
            <span>{value}</span>
          </div>
        ))}
      </div>

      <div className="title-screen-top">
        <motion.h1
          className="title-screen-heading"
          animate={{ scale: [1, 1.03, 1] }}
          transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
        >
          TAVELA
        </motion.h1>
      </div>

      <div className="title-screen-middle">
        <p className="title-screen-instructions">
          Match numbers or add to 10 to clear.
          <br />
          Keep the flow alive.
        </p>
      </div>

      <div className="menu-actions title-screen-actions">
        <button type="button" className="menu-button menu-button-primary" onClick={onStartRun}>
          START RUN
        </button>
        <button
          type="button"
          className="menu-button"
          onClick={onOpenLeaderboard}
          disabled={!isLeaderboardEnabled}
        >
          LEADERBOARD
        </button>
        <button type="button" className="menu-button menu-button-secondary" onClick={onOpenSettings}>
          SETTINGS
        </button>
      </div>

      <SupportLinks
        className="title-screen-links"
        discordUrl={discordUrl}
        feedbackUrl={feedbackUrl}
        includeLegalLinks={false}
        supportEmail={supportEmail}
        onOpenLink={onOpenLink}
      />
    </motion.section>
  );
}
